import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../api/client';
import { DataTable, type Column } from '../components/DataTable';

interface WebhookItem {
  id: string;
  source: string;
  externalId: string;
  status: string;
  attempts: number;
  lastError: string | null;
  receivedAt: string;
  processedAt: string | null;
}

interface WebhookPage {
  items: WebhookItem[];
  total: number;
}

const STATUS_OPTIONS = ['', 'received', 'processing', 'done', 'failed', 'dead_letter'];

export function WebhooksPage(): JSX.Element {
  const [status, setStatus] = useState('');
  // Mostrava só os 25 primeiros sem dizer que havia mais (achado D10-02). O
  // backend já paginava; o painel é que nunca pedia página nem lia o total.
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const queryClient = useQueryClient();

  useEffect(() => {
    setPage(1);
  }, [status]);

  const { data, isLoading, isError, refetch } = useQuery<WebhookPage>({
    queryKey: ['webhooks', status, page],
    queryFn: () => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      return request<WebhookPage>(`/admin/webhooks?${p.toString()}`);
    },
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const retry = useMutation({
    mutationFn: (id: string) => request(`/admin/webhooks/${id}/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const columns: Column<WebhookItem>[] = [
    { key: 'source', header: 'Origem' },
    {
      key: 'externalId',
      header: 'External ID',
      render: (r) => <code className="text-xs">{r.externalId}</code>,
    },
    { key: 'status', header: 'Status' },
    { key: 'attempts', header: 'Tentativas' },
    {
      key: 'lastError',
      header: 'Último erro',
      render: (r) =>
        r.lastError ? <span className="text-sara-danger text-xs">{r.lastError}</span> : '—',
    },
    {
      key: 'receivedAt',
      header: 'Recebido em',
      render: (r) => new Date(r.receivedAt).toLocaleString('pt-BR'),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (r) =>
        ['failed', 'dead_letter'].includes(r.status) ? (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => retry.mutate(r.id)}
            disabled={retry.isPending}
          >
            Retry
          </button>
        ) : null,
    },
  ];

  return (
    <section aria-labelledby="webhooks-heading" className="space-y-4">
      <h1 id="webhooks-heading" className="text-2xl font-semibold">
        Webhooks
      </h1>

      <label className="block">
        <span className="label">Filtro de status</span>
        <select
          className="input max-w-xs"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || 'Todos'}
            </option>
          ))}
        </select>
      </label>

      <DataTable
        columns={columns}
        rows={data?.items}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        rowKey={(r) => r.id}
      />
      {total > 0 && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-sara-text-muted">{total} evento(s) neste filtro</span>
          {totalPages > 1 && (
            <>
              <button
                type="button"
                className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← anterior
              </button>
              <span className="text-sara-text-muted">
                página {page} de {totalPages}
              </span>
              <button
                type="button"
                className="px-2 py-1 rounded bg-sara-surface disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                próxima →
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
